import { useTranslation } from 'react-i18next';
import { ClientOnly } from 'remix-utils/client-only';
import type { ProductListModule } from 'types/capi/article';
import type { Affiliate, Image as ImageType } from 'types/capi/shared';

import AffiliateProductLink from '~/components/AffiliateProductLink';
import { InternalLink } from '~/components/Button';
import Countdown from '~/components/Countdown';
import Image from '~/components/Image';
import Price from '~/components/Price';
import ProductCard from '~/components/ProductCard';
import ProductNotificationButton from '~/components/ProductNotificationButton';
import type { StockStatus as StockStatusType } from '~/domain/stockStatus';
import { getStockStatus, textColours } from '~/domain/stockStatus';
import useUser from '~/hooks/useUser';
import { formatReleaseDateLong } from '~/utils/dates';
import getHexBrightness from '~/utils/hexBrightness';

export default function ProductList(props: ProductListModule) {
  switch (props.display_type) {
    case 'carousel':
      return <ProductListCarousel {...props} />;
    case 'grid':
      return <ProductListGrid {...props} />;
    case 'block':
      return (
        <ProductListBlock
          backgroundImage={props.background_image}
          backgroundColor={props.background_colour}
          product={props.items[0]!}
        />
      );
    default:
      return null;
  }
}

function ProductListCarousel({
  header,
  description,
  items,
  background_colour = '#FAFAFA'
}: {
  background_colour?: string;
  description?: string;
  header?: string;
  items: ProductListModule['items'];
}) {
  const bgBrightness = getHexBrightness(background_colour);
  const shouldCentreItems = items.length < 4;

  return (
    <div
      className={`col-span-full w-full px-4 py-8 md:px-10 ${bgBrightness === 'dark' ? 'text-white' : 'text-black'}`}
      style={{ backgroundColor: background_colour }}
    >
      <div className="mx-auto flex h-fit max-w-6xl flex-col items-center gap-6">
        {(header || description) && (
          <div className="flex w-full flex-col items-center gap-2 text-center">
            {header && (
              <div className="text-[32px] font-normal leading-[48px] tracking-[0.05rem] md:text-[40px]">
                {header}
              </div>
            )}
            {description && <div className="text-sm">{description}</div>}
          </div>
        )}
        <div
          className={`custom-scrollbar flex w-full overflow-x-scroll ${
            shouldCentreItems ? 'md:justify-center md:overflow-x-visible' : ''
          } md:px-0`}
        >
          <div className="flex shrink-0 gap-2 md:gap-6">
            {items.map((product) =>
              product.type === 'external' ? (
                <ProductCard.External
                  allowNotifications
                  cardSize="small"
                  name={`PRODUCT_CARD_${header}`}
                  className="h-auto w-[calc(50vw-24px)] min-w-[140px] max-w-[360px] shrink-0 md:w-[285px] md:min-w-[285px]"
                  imageLoading="eager"
                  key={product.id}
                  openPdp={false}
                  padImage
                  pagePosition="Product Carousel"
                  product={product}
                />
              ) : (
                <ProductCard.Internal
                  allowNotifications
                  cardSize="small"
                  name={`PRODUCT_CARD_${header}`}
                  className="h-auto w-[calc(50vw-24px)] min-w-[140px] max-w-[360px] shrink-0 md:w-[285px] md:min-w-[285px]"
                  imageLoading="eager"
                  key={product.id}
                  padImage
                  pagePosition="Product Carousel"
                  product={product}
                />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductListGrid({
  header,
  description,
  items,
  background_colour = '#2A2528'
}: {
  background_colour?: string;
  description?: string;
  header?: string;
  items: ProductListModule['items'];
}) {
  const bgBrightness = getHexBrightness(background_colour);
  const shouldCentreItems = items.length < 4;

  const columnClass = shouldCentreItems
    ? `md:grid-cols-${items.length}`
    : 'md:grid-cols-4';

  return (
    <div
      className={`w-full px-4 py-8 md:px-10 ${bgBrightness === 'dark' ? 'text-white' : 'text-black'}`}
      style={{ backgroundColor: background_colour }}
    >
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-6">
        {(header || description) && (
          <div className="flex flex-col items-center gap-2 text-center">
            {header && (
              <h2 className="text-[32px] font-normal leading-[48px] tracking-[0.05rem] md:text-[40px]">
                {header}
              </h2>
            )}
            {description && (
              <p
                className={`text-sm ${bgBrightness === 'dark' ? 'text-gray-300' : 'text-black'}`}
              >
                {description}
              </p>
            )}
          </div>
        )}
        <div
          className={`w-full ${shouldCentreItems ? 'flex justify-center' : ''}`}
        >
          <div
            className={`grid grid-cols-2 ${columnClass} gap-x-2 bg-inherit md:gap-x-5`}
          >
            {items.map((product) =>
              product.type === 'external' ? (
                <ProductCard.External
                  allowNotifications
                  cardSize="small"
                  name={`PRODUCT_CARD_${header}`}
                  className="h-auto w-[calc(50vw-24px)] min-w-[140px] max-w-[360px] shrink-0 md:w-[285px] md:min-w-[285px]"
                  imageLoading="eager"
                  key={product.id}
                  openPdp={false}
                  padImage
                  pagePosition="Product Grid"
                  product={product}
                />
              ) : (
                <ProductCard.Internal
                  allowNotifications
                  cardSize="small"
                  name={`PRODUCT_CARD_${header}`}
                  className="h-auto w-[calc(50vw-24px)] min-w-[140px] max-w-[360px] shrink-0 md:w-[285px] md:min-w-[285px]"
                  imageLoading="eager"
                  key={product.id}
                  padImage
                  pagePosition="Product Grid"
                  product={product}
                />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductListBlock({
  backgroundImage,
  backgroundColor,
  product
}: {
  backgroundColor?: string;
  backgroundImage?: ImageType;
  product: ProductListModule['items'][0];
}) {
  return (
    <Backdrop image={backgroundImage} backgroundColor={backgroundColor}>
      <div className="flex w-full max-w-[552px] flex-col gap-4">
        <span className="text-[32px] text-white lg:text-[40px]">
          {product.name}
        </span>
        <div className="rounded-lg bg-white px-3 py-4">
          {['in-stock', 'reseller'].includes(product.stock_status) ? (
            <InStockProductInfo product={product} />
          ) : (
            <OutOfStockProductInfo product={product} />
          )}
        </div>
      </div>
      <div className="w-full max-w-[360px]">
        {product.type === 'external' ? (
          <ProductCard.External
            allowNotifications
            cardSize="large"
            padImage
            openPdp={false}
            pagePosition="Featured Product Block"
            product={product}
            withMetadata={false}
          />
        ) : (
          <ProductCard.Internal
            allowNotifications
            cardSize="large"
            padImage
            pagePosition="Featured Product Block"
            product={product}
            withMetadata={false}
          />
        )}
      </div>
    </Backdrop>
  );
}

function Backdrop({
  children,
  image,
  backgroundColor
}: {
  backgroundColor?: string;
  children: React.ReactNode;
  image?: ImageType;
}) {
  return (
    <div className="relative z-10 w-full px-4 py-8 md:px-10">
      {image ? (
        <Image
          loading="eager"
          className="absolute inset-0 -z-40 block size-full object-cover"
          alt={image.alt}
          src={image.url}
        />
      ) : (
        <div
          className="absolute inset-0 -z-40 block size-full"
          style={{ backgroundColor: backgroundColor ?? '#000000' }}
        />
      )}
      <div className="absolute inset-0 -z-30 bg-black opacity-40"></div>
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 md:flex-row">
        {children}
      </div>
    </div>
  );
}

function InStockProductInfo({
  product
}: {
  product: ProductListModule['items'][0];
}) {
  return (
    <div className="flex w-full flex-col gap-4 rounded-md bg-white p-1">
      <div className="mb-2 flex w-full flex-row items-center justify-between">
        <div className="flex items-center gap-1">
          <AffiliateLogo affiliate={product.affiliate} />
          <StockStatus status={product.stock_status} />
        </div>
        <Price {...product.price} className="flex gap-1" />
      </div>
      <InStockSizes product={product} />
      {product.type === 'external' ? (
        <AffiliateProductLink
          className="flex h-12 items-center justify-center"
          affiliate={product.affiliate}
          pagePosition="Featured Product Block"
          buttonPosition={-1}
          price={product.price}
          product={{
            name: product.name,
            price: product.price,
            product_id: product.id
          }}
          urlTemplates={{
            click_ref_template: product.click_ref_template,
            url: product.url,
            url_template: product.url_template
          }}
          variant="forest"
        >
          Buy now from {product.affiliate.name}
        </AffiliateProductLink>
      ) : (
        <InternalLink to={product.path} variant="forest">
          Buy now from {product.affiliate.name}
        </InternalLink>
      )}
    </div>
  );
}

function OutOfStockProductInfo({
  product
}: {
  product: ProductListModule['items'][0];
}) {
  const isNotified = useUser().isNotificationSetForProduct(product.legacy_id);
  const stockStatus = getStockStatus(product.stock_status);

  const isUpcoming =
    stockStatus === 'coming-soon' || stockStatus === 'upcoming';

  return (
    <div className="flex w-full flex-col gap-4 rounded-md bg-white p-1">
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-start">
          <StockStatus status={product.stock_status} />
          {isUpcoming && product.date.released && (
            <div className="text-sm font-light text-grey-read">
              {formatReleaseDateLong(product.date.released)}
            </div>
          )}
        </div>
        {isUpcoming && product.date.released && (
          <ClientOnly>
            {() => <Countdown toUnixTimestamp={product.date.released!} />}
          </ClientOnly>
        )}
      </div>
      <div className="flex w-full flex-col gap-2">
        <ProductNotificationButton
          product={{
            affiliateName:
              product.type === 'external' ? product.affiliate.name : undefined,
            id: product.id,
            image: product.image,
            legacyId: product.legacy_id,
            name: product.name,
            price: product.price.regular
          }}
          className="h-12 w-full"
          pagePosition="Dynamic Product Block"
          buttonStyle="normal"
        />
        <NotificationTooltip
          stockStatus={getStockStatus(product.stock_status)}
          isNotified={isNotified}
        />
      </div>
    </div>
  );
}

function AffiliateLogo({ affiliate }: { affiliate: Affiliate }) {
  return (
    <Image
      className="w-14"
      src={affiliate.logo}
      loading="lazy"
      alt={`${affiliate.name}-logo`}
      responsiveOptions={{
        xs: { width: 150 }
      }}
    />
  );
}

const InStockSizes = ({
  product
}: {
  product: ProductListModule['items'][0];
}) => {
  if (!product.sizes?.length) return null;

  return (
    <ul className="flex	flex-wrap items-center gap-1">
      <li className="mr-2 text-xs tracking-wide text-grey-hide">
        UK sizes in stock:
      </li>
      {product.sizes.map((size) =>
        product.type === 'external' ? (
          <AffiliateProductLink
            key={`sl-size-${size}`}
            affiliate={product.affiliate}
            price={product.price}
            product={product}
            buttonPosition={-1}
            pagePosition="Featured Product Block"
            urlTemplates={{
              click_ref_template: product.click_ref_template,
              url: product.url,
              url_template: product.url_template
            }}
            variant="none"
          >
            <Size>{size}</Size>
          </AffiliateProductLink>
        ) : (
          <InternalLink
            key={`sl-size-${size}`}
            to={product.path}
            variant="none"
          >
            <Size>{size}</Size>
          </InternalLink>
        )
      )}
    </ul>
  );
};

function Size({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex h-5 items-center rounded-xl  border border-grey-divide bg-white p-2 text-xs text-grey-hide">
      {children}
    </li>
  );
}

function NotificationTooltip({
  isNotified,
  stockStatus
}: {
  isNotified: boolean;
  stockStatus?: StockStatusType;
}) {
  if (isNotified) return null;

  return (
    <div className="translate-y-[-120%] animate-slide-from-top text-clip">
      <div className="bg-purple-hola-lm p-2 text-center text-xs text-purple-hola">
        {stockStatus === 'sold-out'
          ? 'Get a restock alert'
          : 'Get a release alert'}
      </div>
    </div>
  );
}

const StockStatus = ({ status }: { status: string }) => {
  const { t } = useTranslation();
  const stockStatus = getStockStatus(status);

  if (!stockStatus) return null;

  return (
    <div className={`text-base tracking-wide ${textColours[stockStatus]}`}>
      {t(stockStatus)}
    </div>
  );
};
